<script>
import {
  pageInfosEtServicesProspectusProps,
  usePageInfosEtServicesProspectus
} from "./mixin";
import Prospectus from "@/components/molecules/prospectus";
import footerCodes from "@/utils/footer-codes";
import Titre from "@/components/atoms/titre";
import LazyImage from "@/components/atoms/lazy-image";
import Carousel from "@/components/organisms/carousel";
import CarouselSlide from "@/components/organisms/carousel/carousel-slide";

import qrcodeProspectus from "@/assets/images/qrcode-prospectus.png";
import whatapp from "@/assets/images/mon-compte/icons/whatapp.svg";
import coraLogo from "@/assets/images/cora-logo.svg";

export default {
  components: {
    Prospectus,
    Titre,
    LazyImage,
    Carousel,
    CarouselSlide
  },
  props: {
    ...pageInfosEtServicesProspectusProps()
  },
  setup() {
    const { isNational } = usePageInfosEtServicesProspectus();
    return {
      isNational,
      qrcodeProspectus,
      whatapp,
      coraLogo
    };
  },
  data() {
    return {
      footerCodes,
      breadcrumbs: [{ text: "Prospectus", route: { link: "#" } }]
    };
  }
};
</script>

<template>
  <div class="c-prospectus-borne">
    <div class="c-prospectus-borne__header">
      <h1 class="c-prospectus-borne__title">
        Consultez les prospectus
      </h1>

      <div class="c-prospectus-borne__messagerie">
        <div class="c-prospectus-borne__messagerie-logo">
          <LazyImage
            :src="whatapp"
            width="60"
            height="60"
          />
        </div>
        <p>Vos prospectus directement dans votre messagerie Whatsapp !</p>
        <LazyImage
          :src="qrcodeProspectus"
          width="74"
          height="74"
          class="u-flex-shrink-none"
        />
      </div>
    </div>
    <div
      v-if="prospectus.length > 0"
      class="c-prospectus-borne__slider-wrapper"
    >
      <Carousel
        variant="borne-prospectus"
        :elements-per-slide="'auto'"
        :navigation-arrows="true"
        :space-between="32"
        :watch-overflow="true"
        :center-insufficient-slides="true"
        :free-mode="true"
        :scroll-bar="true"
        navigation-icon-size="xxlg30"
        navigation-size="xlg"
      >
        <CarouselSlide
          v-for="item in prospectus"
          :key="item.id"
        >
          <Prospectus
            img-width="544"
            :img-path="item.attributes.cover"
            :period="item.attributes.period"
            :title="item.attributes.title"
            :link="item.attributes.url"
            :can-add-to-cart="item.attributes.can_add_to_cart"
            :is-national="isNational"
            :start-date="item.attributes.start_at"
            borne
            large
            class="u-full-height"
          />
        </CarouselSlide>
      </Carousel>
    </div>
    <div
      v-else
      class="u-bgcolor-white u-ml-md u-mr-md u-mb-md u-mt-sm u-m-none@sm u-p-md u-radius-md u-radius-none@sm"
    >
      <div class="u-flex u-flex-wrap u-flex-justify-center">
        <Titre
          variant="primary"
          size="md"
          tag="h1"
          class="u-text-center u-mb-md"
        >
          Aucun prospectus n'est disponible pour le moment
        </Titre>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.c-prospectus-borne {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.c-prospectus-list-header {
  background-color: #fff;
  padding-bottom: spacing(lg);
}

.c-prospectus-list-header__title-wrapper {
  display: flex;
  display: inline-flex;
  align-items: baseline;
  column-gap: spacing(xs);
  margin-bottom: spacing(sm);
  margin-top: spacing(xs);

  @include mq(bp(sm)) {
    flex-direction: column;
  }
}

.c-prospectus-list-header__title {
  font-family: font(secondary);
  font-size: fontSize(28);
  font-weight: 700;
}
.c-prospectus-list-header__divider-title {
  @include mq(bp(sm)) {
    display: none;
  }
}
.c-prospectus-list-header__total {
  font-size: fontSize(18);
  font-weight: 500;
}
.c-prospectus-list-header__text {
  font-size: fontSize(14);
}

.c-prospectus-list-header__newsletter {
  display: flex;
  justify-content: space-between;
  border: 2px solid color(primary);
  padding: spacing(sm) spacing(lg) spacing(sm) 2rem;
  max-width: 92.8rem;
  width: 100%;
  border-radius: radius(md);
  margin-top: spacing(lg);

  @include mq(bp(md)) {
    padding: spacing(sm) spacing(md);
    flex-direction: column;
    align-items: center;
  }

  .c-prospectus-list-header__newsletter-content {
    display: flex;
    gap: spacing(md);

    @include mq(bp(md)) {
      flex-direction: column;
      align-items: center;
      margin-bottom: spacing(sm);
    }
  }

  .c-prospectus-list-header__newsletter-text {
    font-size: fontSize(14);
  }

  .c-prospectus-list-header__newsletter-button-wrapper {
    display: flex;
    gap: 1.2rem;
    @include mq(340px) {
      flex-wrap: wrap;
      justify-content: center;
    }
  }
}

.c-prospectus-list__list-wrapper {
  max-width: 1360px;
  padding: 0 spacing(sm);
  margin: 0 auto;
  padding-top: spacing(sm);
  padding-bottom: 7rem;
}

.c-prospectus-list__list {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(31.6rem, max-content));
  justify-content: center;
  gap: spacing(sm);
  @include mq(bp(md)) {
    display: flex;
    flex-wrap: wrap;
  }
}

.c-prospectus-borne__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  padding: 3.2rem 5.6rem;
  gap: 1.6rem;
}

.c-prospectus-borne__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0 2.4rem;
  font-family: font(secondary);
  font-size: 6rem;
  font-weight: 700;
}

.c-prospectus-borne__messagerie {
  position: relative;
  display: flex;
  align-items: center;
  max-width: 55.2rem;
  width: 100%;
  padding: 1.2rem 1.6rem 1.2rem 5.6rem;
  font-size: 2.4rem;
  font-weight: 500;
  border: 1px solid color(grey-5);
  background: color(white);
  margin-top: 1.2rem;
  margin-left: 5.5rem;
  gap: 1.6rem;
  border-radius: 1.6rem;
}

.c-prospectus-borne__messagerie-logo {
  position: absolute;
  top: -1.2rem;
  left: -5.5rem;
  padding: 1.2rem;
  border-radius: 2.4rem;
  background-color: color(white);
  border: 1px solid color(border);
  transform: rotate(-5.72deg);
}
.c-prospectus-borne__slider-wrapper {
  margin-top: auto;
}
</style>
